import { createAxiosInstance } from './axios';
import { AxiosRequestConfig } from 'axios';
import qs from 'qs';

const axios = createAxiosInstance({
  baseURL: '/api/studykit',
  // Make sure url params get serialized in similar way as express expects them (when using array values as params)
  paramsSerializer: (params: any) => {
    if (params instanceof URLSearchParams) return params.toString();
    return qs.stringify(params, { arrayFormat: 'repeat' });
  },
});

export interface Study {
  id: string;
  name: string;
  organization: string;
  start: string;
  end: string;
  numberOfSubjects: number;
  status?: 'ongoing' | 'completed';
  blinded: boolean;
}

export interface Devices {
  id: string;
  name: string;
  type: string;
  serial?: string;
  macAddress?: string;
  bodyPosition: string;
  usageStart: Date;
  usageEnd: Date;
  lastSync?: number;
  softwareVersion?: string;
  hardwareVersion?: string;
}

export interface Subject {
  id: string;
  name: string;
  gender?: 'male' | 'female' | 'other' | 'unknown';
  dateOfBirth?: string;
  weightKg?: number;
  heightCm?: number;
  handedness?: 'left' | 'right' | 'unknown';
  startDate?: string;
  endDate?: string;
  activityLast14Days?: boolean[];
}

export interface Algorithm {
  name: string;
  repository: string;
  privateKey: any;
}

export interface Configuration {
  name: string;
  algorithm: {
    name: string;
    version: string;
  };
  trigger:
    | {
        type: 'manual';
      }
    | {
        type: 'interval';
        params: { cron: string };
      };
}

export interface MeasurementType {
  name: string;
  label: string;
  sourceFormat: string;
  sourceFormatId: number;
  fields: string[];
  abbreviation: string;
}

export interface FailedImport {
  error: {
    name: string;
    errorCode: number;
    validationError: ValidationError;
    clientVersion: string;
    fileName: string;
    message: string;
  };
}

export interface SuccessfulImport {
  totalRows: number;
  id: number;
  fixedIntervalInMillis: number;
  subjectId: string;
  metadataHash: string;
  measurementType: string;
  sourceFormatId: string;
  sourceFormat: string;
  dataSourceId: number;
  createdAt: Date;
  updatedAt: Date;
}

export type ImportResult =
  | { data: FailedImport; result: 'failed'; fileName: string }
  | { data: SuccessfulImport; result: 'success'; fileName: string };

export interface ValidationError {
  name: string;
  row: number;
  column: number;
  columnName: string;
  value: string;
  message: string;
}

export interface Period {
  startAt: Date;
  endAt: Date;
  metadataId: number;
}

export interface User {
  groups: string[];
  email: string;
}

export type CreateUserDetails = {
  email: string;
  roles: string[];
};

export type ConfigurationWithId = Configuration & {
  id: string;
};

export type AlgorithmWithId = Omit<Algorithm, 'privateKey'> & { id: number; versions: string[] };

// Shape returned by the EMS /algorithms endpoint (used for study-scoped algorithm listing)
export type AlgorithmWithVersions = { model: string; versions: string[] };

export enum BODY_POSITIONS {
  'Unspecified' = 0,
  'Wrist left' = 1,
  'Wrist right' = 2,
  'Wrist unspecified' = 3,
  'Upper arm left' = 4,
  'Upper arm right' = 5,
  'Upper arm unspecified' = 6,
  'Anklet left' = 7,
  'Ankle right' = 8,
  'Ankle unspecified' = 9,
  'Chest' = 10,
  'Temple left' = 11,
  'Temple right' = 12,
  'Temple unspecified' = 13,
  'Thigh left' = 14,
  'Thigh right' = 15,
  'Thigh unspecified' = 16,
}

export const getStudies = (): AxiosRequestConfig<Study[]> => {
  return {
    url: '/studies',
    method: 'GET',
  };
};

export const getSingleStudy = (studyId: string): AxiosRequestConfig<Study> => {
  return { url: `/studies/${studyId}`, method: 'GET' };
};

export const getUsers = (studyId: string): AxiosRequestConfig<User[]> => {
  return {
    url: `/studies/${studyId}/users`,
    method: 'GET',
  };
};

export const getSubjects = (studyId: string): AxiosRequestConfig<Subject[]> => {
  return {
    url: `/studies/${studyId}/subjects`,
    method: 'GET',
  };
};

export const getSubject = (studyId: string, subjectId: string): AxiosRequestConfig<Subject> => {
  return {
    url: `/studies/${studyId}/subjects/${subjectId}`,
    method: 'GET',
  };
};

export const getSubjectDevices = (
  studyId: string,
  subjectId: string,
): AxiosRequestConfig<Devices[]> => {
  return {
    url: `/studies/${studyId}/subjects/${subjectId}/devices`,
    method: 'GET',
  };
};

export const createStudy = async (data: Study): Promise<Study> => {
  const study = await axios.post<Study>('/studies', data);
  return study.data;
};

export const createSubject = async (studyId: string, data: Subject): Promise<Subject> => {
  return await axios.post(`/studies/${studyId}/subjects`, data);
};

export const createDevice = async (
  studyId: string,
  subjectId: string,
  data: Devices,
): Promise<Devices> => {
  return await axios.post(`/studies/${studyId}/subjects/${subjectId}/devices`, data);
};

export const deleteStudy = async (studyId: string) => {
  return await axios.delete(`/studies/${studyId}`);
};

export const deleteSubject = async (studyId: string, subjectId: string) => {
  return await axios.delete(`/studies/${studyId}/subjects/${subjectId}`);
};

export const deleteDevice = async (studyId: string, subjectId: string, deviceId: string) => {
  return await axios.delete(`/studies/${studyId}/subjects/${subjectId}/devices/${deviceId}`);
};

export const updateSubject = async (studyId: string, subjectId: string, data: Subject) => {
  return await axios.put(`/studies/${studyId}/subjects/${subjectId}`, data);
};

export const updateDevice = async (
  studyId: string,
  subjectId: string,
  deviceId: string,
  data: Devices,
) => {
  return await axios.put(`/studies/${studyId}/subjects/${subjectId}/devices/${deviceId}`, data);
};

export const updateStudy = async (data: Study): Promise<Study> => {
  return await axios.put(`/studies/${data.id}`, data);
};

export const createAlgorithm = async (data: Algorithm): Promise<AlgorithmWithId> => {
  const algorithm = new FormData();
  algorithm.append('name', data.name);
  algorithm.append('repository', data.repository);
  algorithm.append('privateKey', data.privateKey);
  const response = await axios.post<AlgorithmWithId>('/algorithms', algorithm);
  return response.data;
};

export const deleteAlgorithm = async (algorithmId: number): Promise<void> => {
  await axios.delete(`/algorithms/${algorithmId}`);
};

export const getAlgorithmsAxios = (): AxiosRequestConfig<AlgorithmWithId[]> => {
  return {
    url: '/algorithms',
    method: 'GET',
  };
};

export const getStudyAlgorithmsAxios = (studyId: string): AxiosRequestConfig<AlgorithmWithVersions[]> => {
  return {
    url: `/studies/${studyId}/algorithms`,
    method: 'GET',
  };
};

export const getAlgorithms = async (): Promise<AlgorithmWithId[]> => {
  const response = await axios.get<AlgorithmWithId[]>('/algorithms');
  return response.data;
};

export const createConfiguration = async (
  studyId: string,
  data: Configuration,
): Promise<ConfigurationWithId> => {
  const response = await axios.post<ConfigurationWithId>(
    `/studies/${studyId}/configurations`,
    data,
  );
  return response.data;
};

export const getConfigurationsForStudyRequest = (
  studyId: string,
): AxiosRequestConfig<ConfigurationWithId[]> => {
  return {
    url: `/studies/${studyId}/configurations`,
    method: 'GET',
  };
};

export const deleteConfiguration = async (
  studyId: string,
  configurationId: string,
): Promise<void> => {
  await axios.delete(`/studies/${studyId}/configurations/${configurationId}`);
};

export const updateConfiguration = async (
  studyId: string,
  configurationId: string,
  data: Configuration,
): Promise<ConfigurationWithId> => {
  const response = await axios.put<ConfigurationWithId>(
    `/studies/${studyId}/configurations/${configurationId}`,
    data,
  );
  return response.data;
};

export const importData = async (
  studyId: string,
  subjectId: string,
  files: Blob[],
): Promise<ImportResult[]> => {
  const formData = new FormData();
  files.forEach((f) => formData.append('file', f));
  const response = await axios.post(
    `/studies/${studyId}/subjects/${subjectId}/data/import`,
    formData,
  );
  return response.data;
};

export const exportData = (
  studyId: string,
  subjectId: string,
  formatIds: number[],
  startAt: Date,
  endAt: Date,
): Promise<Blob> => {
  return axios
    .get(`/studies/${studyId}/subjects/${subjectId}/data/export`, {
      params: {
        startAt,
        endAt,
        formatId: formatIds,
      },
      responseType: 'blob',
    })
    .then((r) => r.data);
};

export interface SyncState {
  state: 'idle' | 'running' | 'failed' | 'queued' | 'success';
  configId: string;
  subjectId: string;
  nextScheduledRun: Date | null;
  configName: string;
  algorithm: Omit<AlgorithmWithId, 'versions'> & { version: string };
  syncedTill: Date | null;
}

export const getSyncStatesRequest = (
  studyId: string,
  subjectId: string,
): AxiosRequestConfig<SyncState[]> => {
  return {
    url: `/studies/${studyId}/subjects/${subjectId}/data/sync-states`,
    method: 'GET',
  };
};

export const triggerConfigurationRun = (studyId: string, subjectId: string, configId: string) => {
  return axios.get(`/studies/${studyId}/subjects/${subjectId}/data/trigger`, {
    params: {
      configId,
    },
  });
};

type TriggerConfigurationResult = {
  configId: string;
  configName: string;
} & (
  | {
      result: 'success';
    }
  | {
      result: 'failed';
      error: string;
    }
);

export const triggerAllForSubject = (studyId: string, subjectId: string) => {
  return axios.get<TriggerConfigurationResult[]>(
    `/studies/${studyId}/subjects/${subjectId}/data/trigger`,
  );
};

export const getMeasurementTypes = (studyId: string, subjectId: string) => {
  return axios
    .get<MeasurementType[]>(`/studies/${studyId}/subjects/${subjectId}/data/measurementTypes`)
    .then((r) => r.data);
};

interface QueryParams {
  measurementType: string;
  bucketDuration: number;
  bucketDurationUnit: 'second' | 'minute' | 'hour' | 'day';
  startAt: Date;
  endAt: Date;
  aggregate: string;
  fillGaps: boolean;
  minimumQualityThreshold?: number;
  groupBy?: string;
  filter?: string[];
}

export const querySubjectData = (
  studyId: string,
  subjectId: string,
  params: QueryParams,
  abortSignal?: AbortSignal,
) => {
  return axios
    .get<Record<string, any>[]>(`/studies/${studyId}/subjects/${subjectId}/data/query`, {
      params,
      signal: abortSignal,
    })
    .then((r) =>
      r.data.map((v) => {
        v['time'] = new Date(v['time']);
        return v;
      }),
    );
};

interface ActivityParams {
  startAt: Date;
  endAt: Date;
}

export const getSubjectsActivity = (studyId: string, subjectId: string, params: ActivityParams) => {
  return axios
    .get<Record<string, boolean[]>>(`/studies/${studyId}/subjects/${subjectId}/data/activity`, {
      params,
    })
    .then((r) => r.data);
};

export interface AuthPermissions {
  globalPermissions: string[];
  studyPermissions: Record<string, string[]>;
}

export const getAuthPermissions = () => {
  return axios.get<AuthPermissions>('/auth/me').then((r) => r.data);
};

export const updateUserRoles = (studyId: string, userId: string, roles: string[]) => {
  return axios.put(`/studies/${studyId}/users/${userId}/roles`, { roles });
};

export const removeUserFromStudy = (studyId: string, userId: string) => {
  return axios.delete(`/studies/${studyId}/users/${userId}`);
};

export const deleteSubjectData = async (
  studyId: string,
  subjectId: string,
  measurementTypes?: string[],
) => {
  await axios.delete(`/studies/${studyId}/subjects/${subjectId}/data`, {
    params: {
      measurementType: measurementTypes,
    },
  });
};

export const createUser = async (studyId: string, user: CreateUserDetails) => {
  return axios.post<User>(`/studies/${studyId}/users`, user).then((r) => r.data);
};
